import React, {useCallback, useEffect, useMemo, useState} from "react";
import Api from "../../../../core/services/api/api";
import CacheService from "../../../../core/services/cache/cache_service";
import useIsMounted from "../../../hooks/use-is-mounted";
import Dialog from "../../base/dialog";
import {Box, CircularProgress, DialogActions, DialogContent, DialogTitle, Divider, Tooltip} from "@mui/material";
import {Col} from "reactstrap";
import TryAgain from "../../app-specific/try-again";
import classnames from "classnames";
import {Add, ContentCopy, DeleteOutlined, EditOutlined, EmailOutlined, PrintOutlined} from "@mui/icons-material";
import IconButton from "../../base/button/icon_button";
import BizPrintService from "../../../../core/services/biz-print/biz-print-service";
import Button from "../../base/button/button";
import useAccessLevels from "../../../hooks/use-access-levels";
import SendEmailDialog from "../send-email";
import Utils from "../../../../core/services/utils/utils";
import {usePartnerInformation} from "../../../hooks/use-partner-information";
import {useOperatorInformation} from "../../../hooks/use-operator-information";


const getPrintApplicationToken = () => {
    return CacheService.printApplicationToken;
}

const BizPrintTemplatesDialog = ({pageName, pageData, open, close,}) => {
    const [templates, setTemplates] = useState([]);
    const [_applicationData] = useState({});
    const [loading, setLoading] = useState(false);
    const [preparing, setPreparing] = useState(false);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    const partnerInformation = usePartnerInformation();
    const partner = useMemo(() => !partnerInformation ? partnerInformation : ({
        ...partnerInformation,
        logo: (partnerInformation?.configuration?.PartnerLogoUrlBaseAddress ?? '') + (partnerInformation?.logo ?? ''),
    }), [partnerInformation])
    const applicationData = useMemo(() => ({
        ..._applicationData,
        partnerInfo: partner,
    }), [_applicationData, partner])

    /**
     * Fetches the templates of the current page.
     * @type {(function())|*}
     */
    const getTemplates = useCallback(async () => {
        const forApi = {
            pageName: pageName,
            applicationId: getPrintApplicationToken(),
        }
        setLoading(true);
        const response = await Api.getBizPrintTemplates(forApi);
        if (!isMounted())
            return;
        setLoading(false);
        setTemplates(response?.data ?? []);
    }, [isMounted, pageName])

    /**
     * With each change of the open state of the dialog and getTemplates function:
     * - if the dialog is closed, clear the templates.
     * - if the dialog is open, fetch the templates.
     */
    useEffect(() => {
        if (!open)
            return setTemplates([]);
        getTemplates().then();
    }, [getTemplates, open])


    /**
     * Gets the biz-print url for the template.
     *
     * - fetches the application content from the application data
     * - fetches the page content from the page data
     * - calls the api to get the edit url.
     * @param template the template to get the edit url for.
     * @type {(function(*))|*}
     */
    const getUrlForTemplate = useCallback(async (template, isForEdit) => {
        setPreparing(true);
        const forApi = {
            templateId: template?.id,
            pageName: pageName,
            applicationId: getPrintApplicationToken(),
            pageContent: BizPrintService.generatePageContent(pageName, pageData),
            applicationContent: BizPrintService.generateApplicationContent(applicationData),
            isForEdit: isForEdit,
        }
        const response = await Api.generateBizPrintAccessToken(forApi);
        if (!isMounted())
            return;
        setPreparing(false);
        if (response?.resultFlag) {
            window.open(response?.data?.url, '_blank');
            close();
        }
    }, [applicationData, isMounted, pageData, pageName, close]);

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={'sm'}
                loading={preparing}
                className={'bound-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Available Print Templates
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={''}>
                    <Col xs={12} className={'mt-3 d-flex flex-wrap mb-5'}>
                        {
                            loading
                                ? Array(4).fill(null).map((e, i) => (
                                    <div key={i} className={'loading normal w-100 mb-3'}>
                                        <div/>
                                    </div>
                                ))
                                : <div className={'d-flex flex-column w-100 align-items-center'}>
                                    {
                                        templates?.length < 1 &&
                                        <TryAgain
                                            text={`There is currently no templates submitted. ${operatorAccessLevels.packages.bizPrint.manageTemplates ? 'You can add a new template by clicking the button' +
                                                ' below.' : ''}`}
                                        />
                                    }
                                    {
                                        templates?.map(template => (
                                            <BizPrintTemplate
                                                key={template?.id}
                                                template={template}
                                                pageName={pageName}
                                                pageData={pageData}
                                                applicationData={applicationData}
                                                setTemplates={setTemplates}
                                            />
                                        ))
                                    }
                                    {
                                        operatorAccessLevels.packages.bizPrint.manageTemplates &&
                                        <Button
                                            className='d-flex align-items-center justify-content-center mt-3'
                                            variant={'outlined'}
                                            color={'primary'}
                                            disabled={preparing}
                                            onClick={() => getUrlForTemplate(null, true)}
                                        >
                                            <Add className={'mr-2'}/>
                                            <p>Add a new Template</p>
                                        </Button>
                                    }
                                </div>
                        }
                    </Col>
                </DialogContent>
                <DialogActions/>
            </Dialog>
        </>
    )
}

const BizPrintTemplatePrepActions = {
    edit: 'edit',
    print: 'print',
    email: 'email',
}

const initEmailDialogState = {
    open: false,
    data: {},
}

const BizPrintTemplate = ({template, pageName, pageData, applicationData, setTemplates}) => {
    const operatorAccessLevels = useAccessLevels();
    const [link, accessToken] = useState({edit: null, view: null});
    const [disabled, setDisabled] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [preparingAction, setPreparingAction] = useState(null)
    const [emailDialogState, setEmailDialogState] = useState(initEmailDialogState);
    const isMounted = useIsMounted();
    const operatorInformation = useOperatorInformation();

    /**
     * Fetches the biz-print url for the template.
     * @type {(function(*): Promise<void>)}
     */
    const fetchTemplateLink = useCallback(async (prepAction) => {
        setDisabled(true);
        setPreparingAction(prepAction);
        const isForEdit = prepAction === BizPrintTemplatePrepActions.edit;
        const forApi = {
            templateId: template?.id,
            pageName: pageName,
            applicationId: getPrintApplicationToken(),
            pageContent: BizPrintService.generatePageContent(pageName, pageData),
            applicationContent: BizPrintService.generateApplicationContent(applicationData),
            isForEdit: isForEdit,
        }
        const response = await Api.generateBizPrintAccessToken(forApi);
        if (!isMounted())
            return;
        setDisabled(false);
        setPreparingAction(null);
        if (response?.resultFlag) {
            accessToken(prevState => ({
                ...prevState,
                [isForEdit ? 'edit' : 'view']: response?.data?.url,
            }));
        }
        return response?.data?.url;
    }, [applicationData, isMounted, pageData, pageName, template?.id])

    /**
     * Navigates to the edit template.
     * @type {(function(): Promise<void>)}
     */
    const navigateToEditTemplate = useCallback(async () => {
        const url = await fetchTemplateLink(BizPrintTemplatePrepActions.edit)
        if (!isMounted())
            return;
        if (url) {
            window.open(url, '_blank');
        }
    }, [fetchTemplateLink, isMounted])

    /**
     * Navigates to the print template.
     * @type {function(): Promise<*>}
     */
    const navigateToPrintTemplate = useCallback(async () => {
        const url = await fetchTemplateLink(BizPrintTemplatePrepActions.print)
        if (!isMounted())
            return;
        if (url) {
            window.open(url, '_blank');
        }
    }, [fetchTemplateLink, isMounted])

    /**
     * Removes a template from the list of templates.
     * @type {(function(*): Promise<void>)}
     */
    const removeTemplate = useCallback(async (template) => {
        setRemoving(true);
        const response = await Api.removeBizPrintTemplate(template?.id, getPrintApplicationToken());
        if (!isMounted())
            return;
        setRemoving(false);
        if (response?.resultFlag) {
            setTemplates(prevState => prevState.filter(t => t?.id !== template?.id));
        }
    }, [isMounted, setTemplates]);

    /**
     * Gets the confirmation dialog for removing a template.
     * @type {(function(*): void)}
     */
    const getRemoveTemplateConfirmation = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Are you sure you want to remove <span className={'font-weight-semi-bold'}>{template?.title}</span> from the list of templates?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (v) => v && removeTemplate(template),
        );
    }, [removeTemplate, template]);

    /**
     * Duplicates a template.
     * @type {(function(*): Promise<void>)}
     */
    const duplicateTemplate = useCallback(async () => {
        setDuplicating(true);
        const response = await Api.duplicateBizPrintTemplate({
            pageName: pageName,
            applicationId: getPrintApplicationToken(),
            id: template?.id,
        });
        if (!isMounted())
            return;
        setDuplicating(false);
        if (response?.resultFlag) {
            setTemplates(prevState => [...prevState, response?.data]);
        }
    }, [isMounted, pageName, setTemplates, template?.id]);

    /**
     * Opens the email dialog with the template data.
     * @type {(function(): void)}
     */
    const openEmailDialog = useCallback(async () => {
        const viewLink = link?.view ?? (await fetchTemplateLink(BizPrintTemplatePrepActions.email));
        if (!isMounted())
            return;
        const operator = {
            name: operatorInformation?.name ?? "",
            email: operatorInformation?.email ?? "",
        }
        setEmailDialogState({
            open: true,
            close: () => setEmailDialogState(initEmailDialogState),
            data: {
                subject: template?.title,
                body: `
                <div>
                    <p>By opening the link below, you will be able to view and print your document (${template?.title ?? ""}).</p>
                    <p><a href="${viewLink}" target="_blank">${viewLink}</a></p>
                    <p><br/></p>
                    <p>Thank you.</p>
                     <p><br/></p>
                    <p>${operator.name ?? ""}</p>
                    <p>${applicationData?.partnerInfo?.name ?? ""}</p>
                    <p><a href="${applicationData?.partnerInfo?.website ?? ""}" target="_blank">${Utils.formatLinkUrlDisplay(applicationData?.partnerInfo?.website ?? "")}</a></p>
                    ${operator?.email ? `<p>${operator?.email ?? ""}</p>` : ''}
                </div>`,
                ccEmail: operator?.email ? [operator?.email ?? ""] : [],
                bccEmail: [],
                toEmail: [],
            },
        })
    }, [applicationData?.partnerInfo?.name, applicationData?.partnerInfo?.website, fetchTemplateLink, isMounted, link?.view, operatorInformation?.email, operatorInformation?.name, template?.title])

    return (
        <>
            <div className={classnames('print-template-card')}>
                <div className={'d-flex align-items-center justify-content-between'}>
                    <p className={'title flex-grow-1'}>
                        {template?.title ?? ''}
                    </p>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5}}>
                        <Tooltip title={'Print this template'}>
                            <Box component={'span'}>
                                <IconButton
                                    disabled={disabled}
                                    color={'paper.primary'}
                                    {...(link?.view && {
                                        component: 'a',
                                        href: link?.view,
                                        target: '_blank',
                                    })}
                                    {...(!link?.view && {
                                        onPressed: navigateToPrintTemplate,
                                    })}
                                >
                                    {
                                        preparingAction === BizPrintTemplatePrepActions.print
                                            ? <CircularProgress size={20}/>
                                            : <PrintOutlined className={"icon normal"}/>
                                    }
                                </IconButton>
                            </Box>
                        </Tooltip>
                        {
                            operatorAccessLevels.packages.bizPrint.emailTemplate &&
                            <Tooltip title={'Email this template'}>
                                <Box component={'span'}>
                                    <IconButton
                                        disabled={disabled}
                                        color={'paper.primary'}
                                        onPressed={openEmailDialog}
                                    >
                                        {
                                            preparingAction === BizPrintTemplatePrepActions.email
                                                ? <CircularProgress size={20}/>
                                                : <EmailOutlined className={"icon normal"}/>
                                        }
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        }
                        {
                            operatorAccessLevels.packages.bizPrint.manageTemplates &&
                            <Tooltip title={'Edit this template'}>
                                <Box component={'span'}>
                                    <IconButton
                                        disabled={disabled}
                                        color={'paper.secondary'}
                                        {...(link?.edit && {
                                            component: 'a',
                                            href: link?.edit,
                                            target: '_blank',
                                        })}
                                        {...(!link?.edit && {
                                            onPressed: navigateToEditTemplate,
                                        })}
                                    >
                                        {
                                            preparingAction === BizPrintTemplatePrepActions.edit
                                                ? <CircularProgress size={20}/>
                                                : <EditOutlined className={"icon normal"}/>
                                        }
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        }
                        {
                            operatorAccessLevels.packages.bizPrint.manageTemplates &&
                            <Tooltip title={'Duplicate this template'}>
                                <Box component={'span'}>
                                    <IconButton
                                        disabled={disabled || duplicating}
                                        color={'paper.secondary'}
                                        onPressed={duplicateTemplate}
                                    >
                                        {
                                            duplicating
                                                ? <CircularProgress size={20}/>
                                                : <ContentCopy className={"icon normal"}/>
                                        }
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        }
                        {
                            operatorAccessLevels.packages.bizPrint.manageTemplates &&
                            <Tooltip title={'Remove this template'}>
                                <Box component={'span'}>
                                    <IconButton
                                        disabled={disabled || removing}
                                        color={'paper.error'}
                                        onPressed={getRemoveTemplateConfirmation}
                                    >
                                        {
                                            removing
                                                ? <CircularProgress size={20}/>
                                                : <DeleteOutlined className='icon normal'/>
                                        }
                                    </IconButton>
                                </Box>
                            </Tooltip>
                        }
                    </Box>
                </div>
            </div>
            <SendEmailDialog
                open={emailDialogState.open}
                close={emailDialogState.close}
                data={emailDialogState.data}
            />
        </>
    );
}

export default BizPrintTemplatesDialog;
